import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import App from "./App";
import { studioFrame, useStudioFrameRouting } from "./lib/studioNavigation";
import { AGENT_ID } from "./lib/base";
import { ErrorState, LoadingState } from "./shell/ui";
import { readDeployment, readFiling, type Account, type Deployment, type Filing } from "./hub/api";
import { HubApp } from "./hub/HubApp";
import { APPEARANCE_KEY, AppearanceContext, applyAppearance, readAppearance, writeAppearance, type Appearance, type Theme } from "./lib/appearance";
import { LoadingShell } from "./shell/LoadingShell";

export default function RootApp() {
  useStudioFrameRouting();
  const [deployment, setDeployment] = useState<Deployment | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [filing, setFiling] = useState<Filing | null>(null);
  const [retry, setRetry] = useState(0);
  const accountChanged = useCallback((account: Account | null) => setDeployment({ kind: "hub", account }), []);
  /**
   * The appearance, read once and applied before anything draws.
   *
   * One answer for the whole studio, whichever shell this document is -- the
   * Hub's five pages, an Agent's own, or an Agent embedded in a frame beside
   * another one. It used to be two: a tab-local draft for the Hub shape and the
   * Agent's server-side prefs for the standalone one, which disagreed the moment
   * a studio had both (D653-D655).
   */
  const [appearance, setAppearance] = useState<Appearance>(readAppearance);
  const [systemDark, setSystemDark] = useState(() => Boolean(window.matchMedia?.("(prefers-color-scheme: dark)").matches));
  const pickAppearance = (next: Appearance) => { setAppearance(next); writeAppearance(next); };
  const theme = appearance.theme;
  const pickTheme = (next: Theme) => pickAppearance({ ...appearance, theme: next });
  useLayoutEffect(() => applyAppearance(appearance), [appearance, systemDark]);
  useEffect(() => {
    // Another tab, or a same-origin Agent frame beside this one, chose a colour.
    const changed = (event: StorageEvent) => {
      if (event.key !== null && event.key !== APPEARANCE_KEY) return;
      setAppearance(readAppearance());
    };
    window.addEventListener("storage", changed);
    const media = window.matchMedia?.("(prefers-color-scheme: dark)");
    const systemChanged = () => setSystemDark(Boolean(media?.matches));
    media?.addEventListener?.("change", systemChanged);
    return () => { window.removeEventListener("storage", changed); media?.removeEventListener?.("change", systemChanged); };
  }, []);
  useEffect(() => {
    const controller = new AbortController();
    setError(null);
    readDeployment(controller.signal)
      .then((value) => {
        if (!controller.signal.aborted) setDeployment(value);
      })
      .catch((failure) => {
        if (!controller.signal.aborted) setError(failure);
      });
    return () => controller.abort();
  }, [retry]);
  const signedOutEntry = !AGENT_ID && deployment?.kind === "hub" && deployment.account === null;
  useEffect(() => {
    if (!signedOutEntry) return;
    const controller = new AbortController();
    readFiling(controller.signal).then((value) => { if (!controller.signal.aborted) setFiling(value); });
    return () => controller.abort();
  }, [signedOutEntry]);
  useEffect(() => {
    if (studioFrame() && error) parent.postMessage({ type: "pa.studio.state", state: {
      ready: true, panels: [], version: "entry-error", pendingCount: 0,
    } }, location.origin);
  }, [error]);
  let content;
  if (deployment && (deployment.kind === "standalone" || (AGENT_ID && deployment.account && new URLSearchParams(location.search).has("workspace"))))
    content = <App account={deployment?.kind === "hub" ? deployment.account : null}
      appearance={{ value: theme, onChange: pickTheme }} />;
  else if (error)
    content = (
      <main className="boot-error">
        <ErrorState error={error} onRetry={() => setRetry((n) => n + 1)} />
      </main>
    );
  else if (!deployment)
    content = (
      AGENT_ID ? <LoadingShell label="正在确认账号与工作区" embedded={new URLSearchParams(location.search).has("workspace")} />
        : <main className="boot-error"><LoadingState label="正在确认账号" /></main>
    );
  else content = (
    <HubApp
      account={deployment.account}
      initialAccountVerified
      theme={theme}
      onTheme={pickTheme}
      onAccount={accountChanged}
    />
  );
  return <AppearanceContext.Provider value={{ value: appearance, onChange: pickAppearance }}>
    {content}
    {signedOutEntry && filing ? <footer className="site-filing">
      {filing.url
        ? <a href={filing.url} target="_blank" rel="noopener noreferrer">{filing.text}</a>
        : filing.text}
    </footer> : null}
  </AppearanceContext.Provider>;
}
